import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import Marquee from "react-fast-marquee";
import { QRCodeSVG } from "qrcode.react";
import { ArrowRight, Heart, Sparkles, Users, Camera } from "lucide-react";
import Navbar from "../components/Navbar";
import Footer from "../components/Footer";
import { api } from "../lib/api";

const HERO_BG =
    "https://static.prod-images.emergentagent.com/jobs/f8229704-e9eb-4c8a-95c4-06601c1d1f67/images/6a72b3c6429d0324540cce6673977bdef8abfda3591b29ff769096cec2e1cb63.png";

const SITE_URL = "https://mtmu.therapymgmt.com/";

export default function Landing() {
    const [stats, setStats] = useState({ total_submissions: 0, finalists_count: 0, voting_open: false });

    useEffect(() => {
        api.get("/stats").then(({ data }) => setStats(data)).catch(() => {});
    }, []);

    return (
        <div className="App min-h-screen flex flex-col">
            <Navbar />

            {/* HERO */}
            <section className="relative overflow-hidden grain" data-testid="hero-section">
                <div className="absolute inset-0">
                    <img src={HERO_BG} alt="" className="w-full h-full object-cover opacity-90" />
                    <div className="absolute inset-0 bg-tmc-bg/55" />
                </div>
                <div className="hero-blob bg-[#FFB72E]/55 w-[520px] h-[520px] -top-32 -left-24" />
                <div className="hero-blob bg-[#5FBA47]/45 w-[420px] h-[420px] bottom-[-160px] right-[-80px]" />
                <div className="hero-blob bg-[#3FA9E0]/40 w-[360px] h-[360px] top-1/2 right-[10%]" />
                <div className="hero-blob bg-[#FF7A59]/35 w-[280px] h-[280px] top-[15%] right-[28%]" />
                <div className="confetti" />

                <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-20 pb-28 lg:pt-28 lg:pb-36">
                    <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 items-start">
                        <div className="lg:col-span-8">
                            <div className="label-overline animate-fade-up" data-testid="hero-overline">
                                TMC · 2026 Tribute
                            </div>
                            <h1
                                className="font-serif text-tmc-ink mt-6 text-5xl sm:text-6xl lg:text-7xl leading-[0.95] tracking-tight max-w-4xl animate-fade-up"
                                style={{ animationDelay: "0.1s" }}
                                data-testid="hero-title"
                            >
                                The quiet moments
                                <br />
                                <em className="not-italic font-medium">
                                    <span className="italic" style={{
                                        background: "linear-gradient(90deg, #FFB72E 0%, #FF7A59 35%, #5FBA47 70%, #3FA9E0 100%)",
                                        WebkitBackgroundClip: "text",
                                        WebkitTextFillColor: "transparent",
                                        backgroundClip: "text",
                                    }}>that move us</span>
                                </em>{" "}
                                forward.
                            </h1>
                            <p
                                className="mt-8 text-lg sm:text-xl text-tmc-inkSoft max-w-2xl leading-relaxed animate-fade-up"
                                style={{ animationDelay: "0.2s" }}
                                data-testid="hero-subtitle"
                            >
                                Behind every patient story is a TMC teammate who showed up — with patience,
                                humor, hands, or heart. This is where we honor those moments, together.
                            </p>

                            <div
                                className="mt-10 flex flex-col sm:flex-row gap-4 animate-fade-up"
                                style={{ animationDelay: "0.3s" }}
                            >
                                <Link to="/submit" className="tmc-btn-primary group" data-testid="hero-submit-cta">
                                    Share a Moment
                                    <ArrowRight className="w-4 h-4 transition-transform group-hover:translate-x-1" />
                                </Link>
                                <Link to="/login" className="tmc-btn-secondary" data-testid="hero-vote-cta">
                                    Sign in to vote
                                </Link>
                            </div>
                        </div>

                        {/* QR Code card */}
                        <div
                            className="lg:col-span-4 animate-fade-up"
                            style={{ animationDelay: "0.35s" }}
                            data-testid="hero-qr-card"
                        >
                            <div className="tmc-card p-5 sm:p-6 bg-white/85 backdrop-blur-md max-w-[280px] mx-auto lg:ml-auto">
                                <div className="label-overline">Scan to share</div>
                                <h3 className="font-serif text-xl text-tmc-ink mt-2 leading-snug">
                                    Open this on your phone
                                </h3>
                                <div className="mt-4 flex items-center justify-center bg-white rounded-2xl p-3 border border-tmc-line">
                                    <QRCodeSVG
                                        value={SITE_URL}
                                        size={180}
                                        bgColor="#FFFFFF"
                                        fgColor="#1F1A14"
                                        level="M"
                                        includeMargin={false}
                                        data-testid="hero-qr-svg"
                                    />
                                </div>
                                <p className="mt-3 text-xs text-tmc-muted text-center break-all">
                                    mtmu.therapymgmt.com
                                </p>
                            </div>
                        </div>
                    </div>

                    <div
                        className="mt-16 grid grid-cols-3 gap-4 sm:gap-8 max-w-2xl animate-fade-up"
                        style={{ animationDelay: "0.45s" }}
                    >
                        <Stat icon={<Heart className="w-4 h-4" />} label="Moments shared" value={stats.total_submissions} testid="stat-total" />
                        <Stat icon={<Sparkles className="w-4 h-4" />} label="Finalists" value={stats.finalists_count} testid="stat-finalists" />
                        <Stat
                            icon={<Users className="w-4 h-4" />}
                            label="Voting"
                            value={stats.voting_open ? "Open" : "Soon"}
                            testid="stat-voting"
                        />
                    </div>
                </div>

                {/* Marquee ribbon */}
                <div className="relative border-y border-tmc-line bg-white/60 backdrop-blur-md py-6">
                    <Marquee gradient={false} speed={36}>
                        {Array.from({ length: 4 }).map((_, i) => (
                            <span key={i} className="marquee-text text-3xl sm:text-4xl px-8">
                                Gratitude · Healing · Community · Compassion · Resilience · Care · Joy ·
                            </span>
                        ))}
                    </Marquee>
                </div>
            </section>

            {/* INVITATION (Together Making Changes copy) */}
            <section className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-24 lg:py-32" data-testid="story-section">
                <div className="text-center">
                    <div className="label-overline">An invitation</div>
                    <h2 className="font-serif text-4xl sm:text-5xl text-tmc-ink mt-5 leading-[1.1]">
                        The most powerful moments are often
                        <br className="hidden sm:block" />
                        <em className="not-italic" style={{
                            background: "linear-gradient(90deg, #FFB72E 0%, #FF7A59 50%, #3FA9E0 100%)",
                            WebkitBackgroundClip: "text",
                            WebkitTextFillColor: "transparent",
                            backgroundClip: "text",
                        }}>the simplest ones.</em>
                    </h2>
                </div>

                <div className="mt-14 grid grid-cols-1 md:grid-cols-3 gap-5">
                    {[
                        { icon: "🚶", text: "A patient standing for the first time." },
                        { icon: "🫂", text: "A smile after a hard-fought session." },
                        { icon: "🤝", text: "A team coming together to support one another." },
                    ].map((m, i) => (
                        <div
                            key={i}
                            className="tmc-card p-7 text-center transition-all hover:-translate-y-1 hover:shadow-md"
                            data-testid={`moment-card-${i}`}
                        >
                            <div className="text-3xl">{m.icon}</div>
                            <p className="font-serif italic text-tmc-ink mt-3 text-lg leading-snug">{m.text}</p>
                        </div>
                    ))}
                </div>

                <p className="mt-12 text-center font-serif text-2xl sm:text-3xl text-tmc-terracottaDark italic">
                    These are the Moments That Matter.
                </p>

                <div className="mt-12 max-w-3xl mx-auto space-y-6 text-tmc-inkSoft text-lg leading-relaxed text-center">
                    <p>
                        At <strong className="text-tmc-ink">Together Making Changes</strong>, we believe the most
                        powerful moments are often the simplest ones. We invite you to share a moment that has
                        impacted you, your team, or the patients you serve. Through your story, you're not only
                        capturing something meaningful — you're helping create change.
                    </p>
                    <p className="font-serif italic text-tmc-ink">
                        For each photo submission, we ask for a donation to support Together Making Changes
                        and the lives we impact every day.
                    </p>
                </div>

                <div className="mt-12 flex justify-center">
                    <Link to="/submit" className="tmc-btn-primary" data-testid="story-cta">
                        <Camera className="w-4 h-4" />
                        Share your moment
                    </Link>
                </div>
            </section>

            {/* HOW IT WORKS */}
            <section className="bg-tmc-sand/50 border-y border-tmc-line">
                <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-20">
                    <div className="label-overline">How it works</div>
                    <h2 className="font-serif text-3xl sm:text-4xl text-tmc-ink mt-4 max-w-2xl">
                        Three small steps, one shared celebration.
                    </h2>
                    <div className="mt-14 grid grid-cols-1 md:grid-cols-3 gap-8">
                        {[
                            { n: "01", t: "Share your moment", d: "Tell the story in your own words. Add a photo and we'll handle the rest.", testid: "step-1" },
                            { n: "02", t: "Curated by admins", d: "Our team reads every entry and selects finalists with care.", testid: "step-2" },
                            { n: "03", t: "MLT / SLT Meeting in June", d: "We'll vote on the finalists and select a winner together at our MLT/SLT meeting this June.", testid: "step-3" },
                        ].map((s) => (
                            <div key={s.n} className="tmc-card p-8" data-testid={s.testid}>
                                <div className="font-serif italic text-tmc-terracotta text-3xl">{s.n}</div>
                                <div className="font-serif text-2xl text-tmc-ink mt-3">{s.t}</div>
                                <p className="text-tmc-inkSoft mt-3 leading-relaxed">{s.d}</p>
                            </div>
                        ))}
                    </div>
                </div>
            </section>

            <Footer />
        </div>
    );
}

function Stat({ icon, label, value, testid }) {
    return (
        <div className="border-l-2 border-tmc-terracotta/60 pl-4" data-testid={testid}>
            <div className="flex items-center gap-1.5 text-tmc-muted">
                {icon}
                <span className="label-overline" style={{ fontSize: "0.65rem" }}>
                    {label}
                </span>
            </div>
            <div className="font-serif text-3xl text-tmc-ink mt-2">{value}</div>
        </div>
    );
}
